
import React, { useState, useEffect } from 'react';
import { IconLayers, IconClock, IconPlay, IconPause, IconQuant, IconSearch } from '../components/Icons';
import { analyzeBasketLiquidity } from '../services/geminiService';
import { useAiQ } from '../state/AiQState';
import toast from 'react-hot-toast';

interface BasketItem {
    symbol: string;
    name: string;
    shares: number;
    weight: number;
    price: number;
    status: 'Pending' | 'Working' | 'Filled';
    filledShares: number;
    avgPx: number;
}

const MOCK_BASKET_TECH: BasketItem[] = [
    { symbol: 'NVDA', name: 'NVIDIA', shares: 500, weight: 25, price: 880.50, status: 'Pending', filledShares: 0, avgPx: 0 },
    { symbol: 'AMD', name: 'Advanced Micro', shares: 1200, weight: 15, price: 178.20, status: 'Pending', filledShares: 0, avgPx: 0 },
    { symbol: 'MSFT', name: 'Microsoft', shares: 800, weight: 20, price: 415.10, status: 'Pending', filledShares: 0, avgPx: 0 },
    { symbol: 'AVGO', name: 'Broadcom', shares: 150, weight: 12, price: 1320.00, status: 'Pending', filledShares: 0, avgPx: 0 },
    { symbol: 'INTC', name: 'Intel Corp', shares: 5000, weight: 10, price: 42.50, status: 'Pending', filledShares: 0, avgPx: 0 },
    { symbol: 'MU', name: 'Micron Tech', shares: 1500, weight: 8, price: 95.30, status: 'Pending', filledShares: 0, avgPx: 0 },
    { symbol: 'TSM', name: 'Taiwan Semi', shares: 1000, weight: 10, price: 140.20, status: 'Pending', filledShares: 0, avgPx: 0 },
];

const ProgramTradingView: React.FC = () => {
    const { addTrade } = useAiQ();
    const [basket, setBasket] = useState<BasketItem[]>(MOCK_BASKET_TECH);
    const [algo, setAlgo] = useState('VWAP');
    const [duration, setDuration] = useState('4 Hours');
    const [isExecuting, setIsExecuting] = useState(false);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [aiAdvice, setAiAdvice] = useState('');

    const totalValue = basket.reduce((acc, item) => acc + (item.shares * item.price), 0);
    const fillPct = basket.reduce((acc, item) => acc + (item.filledShares / item.shares), 0) / basket.length * 100;

    // Simulate Execution Logic
    useEffect(() => {
        if (!isExecuting) return;

        const interval = setInterval(() => {
            setBasket(prev => {
                let allFilled = true;
                const updated = prev.map(item => {
                    if (item.filledShares >= item.shares) return { ...item, status: 'Filled' as const };
                    
                    allFilled = false;
                    // Fill rate depends on algo aggressiveness (simulated)
                    const fillRate = Math.floor(Math.random() * 20) + 5; 
                    const newFilled = Math.min(item.shares, item.filledShares + fillRate);
                    
                    // Simulate execution price (slippage)
                    const slippage = (Math.random() - 0.45) * 0.05; // Slight bias to pay more
                    const execPx = item.price + slippage;
                    
                    // Update VWAP
                    const currentTotalVal = (item.avgPx * item.filledShares);
                    const newChunkVal = (execPx * (newFilled - item.filledShares));
                    const newAvg = newFilled > 0 ? (currentTotalVal + newChunkVal) / newFilled : 0;

                    return {
                        ...item,
                        status: 'Working' as const,
                        filledShares: newFilled,
                        avgPx: newAvg
                    };
                });

                if (allFilled) {
                    setIsExecuting(false);
                    toast.success("Basket Execution Complete");
                    // Book the aggregate basket trade to Ops
                    addTrade({
                        id: `PRG-${Math.floor(Math.random()*1000)}`,
                        product: 'Tech Sector Basket',
                        counterparty: 'Algo Engine',
                        notional: totalValue,
                        currency: 'USD',
                        valuation: totalValue,
                        status: 'Settled',
                        tradeDate: new Date().toISOString().split('T')[0],
                        settlementDate: new Date().toISOString().split('T')[0],
                        trader: 'AUTO'
                    });
                }
                return updated;
            });
        }, 500);

        return () => clearInterval(interval);
    }, [isExecuting, addTrade, totalValue]);

    const handleAnalyze = async () => {
        setIsAnalyzing(true);
        setAiAdvice('');
        const advice = await analyzeBasketLiquidity(basket.map(i => ({ symbol: i.symbol, notional: i.shares * i.price })));
        setAiAdvice(advice);
        setIsAnalyzing(false);
    };

    const handleReset = () => {
        setIsExecuting(false);
        setBasket(MOCK_BASKET_TECH);
        setAiAdvice('');
    };

    return (
        <div className="flex h-full bg-slate-100">
            {/* Left: Basket Manager */}
            <div className="w-80 bg-white border-r border-slate-200 flex flex-col p-6 overflow-y-auto custom-scrollbar">
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2 mb-1">
                    <IconLayers /> Program Trading
                </h2>
                <p className="text-xs text-slate-500 mb-6">Basket Algo & List Execution</p>

                <div className="space-y-6">
                    <div className="bg-slate-50 p-4 rounded border border-slate-200">
                        <h3 className="text-xs font-bold text-slate-700 uppercase mb-3">Algo Strategy</h3>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs text-slate-500 mb-1">Algorithm</label>
                                <select 
                                    value={algo} 
                                    onChange={(e) => setAlgo(e.target.value)}
                                    className="w-full border border-slate-300 rounded p-1.5 text-sm outline-none"
                                >
                                    <option>VWAP (Volume Weighted)</option>
                                    <option>TWAP (Time Weighted)</option>
                                    <option>POV (Pct of Volume)</option>
                                    <option>Arrival Price (Aggressive)</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs text-slate-500 mb-1">Duration</label>
                                <select 
                                    value={duration} 
                                    onChange={(e) => setDuration(e.target.value)}
                                    className="w-full border border-slate-300 rounded p-1.5 text-sm outline-none"
                                >
                                    <option>On Close</option>
                                    <option>4 Hours</option>
                                    <option>2 Hours</option>
                                    <option>Immediate</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="bg-blue-50 p-4 rounded border border-blue-200 text-center">
                        <div className="text-xs text-blue-500 uppercase font-bold mb-1">Basket Value</div>
                        <div className="text-2xl font-bold text-blue-900">${(totalValue/1000000).toFixed(2)}M</div>
                        <div className="text-xs text-blue-400 mt-1">{basket.length} Constituents</div>
                    </div>

                    <div className="flex gap-2">
                        <button 
                            onClick={handleAnalyze}
                            disabled={isAnalyzing || isExecuting}
                            className="flex-1 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 font-bold text-xs rounded transition-colors flex items-center justify-center gap-1"
                        >
                            {isAnalyzing ? <span className="animate-spin">⏳</span> : <IconQuant />}
                            Analyze Liquidity
                        </button>
                        <button 
                            onClick={handleReset}
                            className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold text-xs rounded transition-colors"
                        >
                            Reset
                        </button>
                    </div>

                    {aiAdvice && (
                        <div className="bg-slate-800 p-3 rounded text-slate-300 text-xs leading-relaxed border border-slate-700 animate-fade-in">
                            <strong className="text-purple-400 block mb-1">AiQ Pre-Trade Analysis:</strong>
                            {aiAdvice}
                        </div>
                    )}

                    <button 
                        onClick={() => setIsExecuting(!isExecuting)}
                        disabled={basket.every(i => i.status === 'Filled')}
                        className={`w-full py-3 font-bold text-white rounded shadow-lg flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 ${
                            isExecuting 
                            ? 'bg-amber-500 hover:bg-amber-600' 
                            : basket.every(i => i.status === 'Filled') 
                                ? 'bg-slate-400 cursor-not-allowed' 
                                : 'bg-emerald-600 hover:bg-emerald-700'
                        }`}
                    >
                        {isExecuting ? <><IconPause /> Pause Algo</> : <><IconPlay /> Execute Basket</>}
                    </button>
                </div>
            </div>

            {/* Right: Execution Monitor */}
            <div className="flex-1 p-6 flex flex-col overflow-hidden">
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 flex-1 flex flex-col overflow-hidden">
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                        <div className="flex items-center gap-4">
                            <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                <IconClock /> Execution Monitor
                            </h3>
                            {isExecuting && (
                                <span className="text-xs font-bold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded animate-pulse">
                                    ALGO RUNNING
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                            <span className="text-slate-500">Total Progress:</span>
                            <div className="w-32 h-3 bg-slate-200 rounded-full overflow-hidden">
                                <div 
                                    className="h-full bg-blue-600 transition-all duration-500" 
                                    style={{ width: `${fillPct}%` }}
                                ></div>
                            </div>
                            <span className="font-mono font-bold text-slate-700">{fillPct.toFixed(1)}%</span>
                        </div>
                    </div>

                    {/* Grid */}
                    <div className="flex-1 overflow-y-auto custom-scrollbar">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-white text-slate-500 sticky top-0 z-10 shadow-sm">
                                <tr>
                                    <th className="px-6 py-3 font-medium">Symbol</th>
                                    <th className="px-6 py-3 font-medium text-right">Order Qty</th>
                                    <th className="px-6 py-3 font-medium text-right">Filled</th>
                                    <th className="px-6 py-3 font-medium text-center">Progress</th>
                                    <th className="px-6 py-3 font-medium text-right">Avg Px</th>
                                    <th className="px-6 py-3 font-medium">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {basket.map(item => {
                                    const pct = (item.filledShares / item.shares) * 100;
                                    return (
                                        <tr key={item.symbol} className="hover:bg-slate-50 transition-colors">
                                            <td className="px-6 py-3 font-bold text-slate-800">{item.symbol}</td>
                                            <td className="px-6 py-3 text-right font-mono text-slate-600">{item.shares.toLocaleString()}</td>
                                            <td className="px-6 py-3 text-right font-mono text-slate-800">{item.filledShares.toLocaleString()}</td>
                                            <td className="px-6 py-3">
                                                <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                                                    <div 
                                                        className={`h-full transition-all duration-300 ${item.status === 'Filled' ? 'bg-emerald-500' : 'bg-blue-500'}`} 
                                                        style={{ width: `${pct}%` }}
                                                    ></div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-3 text-right font-mono text-slate-600">
                                                {item.avgPx > 0 ? item.avgPx.toFixed(3) : '-'}
                                            </td>
                                            <td className="px-6 py-3">
                                                <span className={`text-[10px] font-bold px-2 py-1 rounded uppercase ${
                                                    item.status === 'Filled' ? 'bg-emerald-100 text-emerald-700' :
                                                    item.status === 'Working' ? 'bg-blue-100 text-blue-700 animate-pulse' :
                                                    'bg-slate-100 text-slate-500'
                                                }`}>
                                                    {item.status}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProgramTradingView;
